import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, apiKeyService } from '@/services/api';
import { Key, Copy, Check, Trash2, Plus, Eye, EyeOff, ArrowLeft, Download, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyData, setNewKeyData] = useState(null);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [copied, setCopied] = useState(null);
  const navigate = useNavigate();

  const downloadGuide = async (format) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/merchant/integration-guide?format=${format}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CauraPay_Guia_Integracion.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading guide:', error);
    }
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const keys = await apiKeyService.getApiKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error('Error loading API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      const newKey = await apiKeyService.createApiKey(newKeyName || 'API Key');
      setNewKeyData(newKey);
      setNewKeyName('');
      loadApiKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('¿Estás seguro de revocar esta API key?')) return;
    
    try {
      await apiKeyService.revokeApiKey(keyId);
      loadApiKeys();
    } catch (error) {
      console.error('Error revoking API key:', error);
    }
  };

  const copyToClipboard = (text, id = 'default') => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#051C2C' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-white hover:text-gray-300"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <img 
                src="/logos/logo-fondo-oscuro.png" 
                alt="Caura Pay" 
                className="h-8"
              />
              <span className="text-xl font-bold text-white border-l border-white/30 pl-4">API Keys</span>
            </div>
            <button
              onClick={() => setShowNewKeyModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="create-api-key-button"
            >
              <Plus className="w-4 h-4" />
              Nueva API Key
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Sobre las API Keys</h3>
              <p className="text-sm text-blue-800">
                Las API keys te permiten integrar el hub de pagos en tus aplicaciones. 
                Guarda tu key de forma segura y nunca la compartas públicamente.
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0 ml-4">
              <button data-testid="download-guide-md" onClick={() => downloadGuide('md')} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-white border border-blue-300 text-blue-800 rounded-lg hover:bg-blue-100">
                <FileText className="w-3.5 h-3.5" /> Guia .md
              </button>
              <button data-testid="download-guide-pdf" onClick={() => downloadGuide('pdf')} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white rounded-lg" style={{ backgroundColor: '#003594' }}>
                <Download className="w-3.5 h-3.5" /> Guia PDF
              </button>
            </div>
          </div>
        </div>

        {/* API Keys List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200" data-testid="api-keys-list">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold" style={{ color: '#051C2C' }}>
              Tus API Keys ({apiKeys.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : apiKeys.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Key className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No tienes API keys todavía</p>
              <p className="text-sm mt-1">Crea una para comenzar a integrar</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {apiKeys.map((key) => (
                <div key={key.key_id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold" style={{ color: '#051C2C' }}>
                          {key.name || 'API Key'}
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          key.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {key.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <code className="text-sm font-mono bg-gray-100 px-3 py-1 rounded">
                          {key.key_prefix}{'*'.repeat(24)}
                        </code>
                        <button
                          data-testid={`copy-key-prefix-${key.key_id}`}
                          onClick={() => copyToClipboard(key.key_prefix, key.key_id)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Copiar prefijo"
                        >
                          {copied === key.key_id ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <p>Creada: {new Date(key.created_at).toLocaleString('es-VE')}</p>
                        {key.last_used && (
                          <p>Último uso: {new Date(key.last_used).toLocaleString('es-VE')}</p>
                        )}
                      </div>
                    </div>
                    {key.status === 'active' && (
                      <button
                        onClick={() => handleRevokeKey(key.key_id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Revocar key"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Usage Example */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold mb-4" style={{ color: '#051C2C' }}>Ejemplo de uso</h3>
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`curl -X POST ${window.location.origin}/api/payments/create \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "amount": 100.00,
    "currency": "VES",
    "payment_method": "banco_plaza_c2p",
    "customer_email": "cliente@ejemplo.com",
    "order_id": "ORDER-123"
  }'`}
          </pre>
        </div>
      </div>

      {/* New Key Modal */}
      {showNewKeyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="new-key-modal">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#051C2C' }}>
              Crear Nueva API Key
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre (opcional)
              </label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Mi API Key"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowNewKeyModal(false);
                  setNewKeyName('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateKey}
                className="flex-1 px-4 py-2 text-white rounded-lg"
                style={{ backgroundColor: '#003594' }}
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Key Success Modal */}
      {newKeyData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4">
            <div className="text-green-500 text-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Key className="w-8 h-8" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-center mb-4" style={{ color: '#051C2C' }}>
              API Key Creada
            </h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800 font-medium mb-2">
                ⚠️ Importante: Copia esta key ahora
              </p>
              <p className="text-xs text-yellow-700">
                Esta es la única vez que verás la key completa. Guárdala en un lugar seguro.
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Tu API Key:</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newKeyData.full_key}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm bg-gray-50"
                />
                <button
                  data-testid="copy-new-full-key"
                  onClick={() => copyToClipboard(newKeyData.full_key, 'new-full-key')}
                  className="px-4 py-2 text-white rounded-lg"
                  style={{ backgroundColor: '#003594' }}
                >
                  {copied === 'new-full-key' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              onClick={() => setNewKeyData(null)}
              className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}